import React, { useRef, useEffect, useState } from "react";
import healthcareHero from "../../assets/hero-banner.jpg";
import { Heart, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const Hero = () => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate('https://pay.livmohealth.com');
  }
  const wrapper1Ref = useRef(null);
  const wrapper2Ref = useRef(null);
  // Refs for magnetic buttons
  const btn1Ref = useRef(null);
  const btn2Ref = useRef(null);
  const [isMagneticEnabled, setIsMagneticEnabled] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsMagneticEnabled(window.innerWidth >= 1024);
    };
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);
  // Simplified magnetic effect
  useEffect(() => {

    if(!isMagneticEnabled) {
      if(btn1Ref.current) btn1Ref.current.style.transform = "";
      if(btn2Ref.current) btn2Ref.current.style.transform = "";
      if(wrapper1Ref.current) wrapper1Ref.current.classList.remove("active");
      if(wrapper2Ref.current) wrapper2Ref.current.classList.remove("active");
      return;
    }

    const setupMagnetic = (wrapper, btn) => {
      if (!wrapper || !btn) return;

      const handleMouseMove = (e) => {
        const rect = btn.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const moveX = (e.clientX - centerX) * 0.15;
        const moveY = (e.clientY - centerY) * 0.15;
        
        const limitedX = Math.min(Math.max(moveX, -12), 12);
        const limitedY = Math.min(Math.max(moveY, -8), 8);
        
        btn.style.transform = `translate(${limitedX}px, ${limitedY}px)`;
        wrapper.classList.add("active"); // Add class to wrapper, not btn
      };

      const handleMouseLeave = () => {
        btn.style.transform = "translate(0, 0)";
        wrapper.classList.remove("active"); // Remove from wrapper
      };

      wrapper.addEventListener("mousemove", handleMouseMove);
      wrapper.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        wrapper.removeEventListener("mousemove", handleMouseMove);
        wrapper.removeEventListener("mouseleave", handleMouseLeave);
      };
    };

    const cleanup1 = setupMagnetic(wrapper1Ref.current, btn1Ref.current);
    const cleanup2 = setupMagnetic(wrapper2Ref.current, btn2Ref.current);

    return () => {
      if (cleanup1) cleanup1();
      if (cleanup2) cleanup2();
    };
  }, [isMagneticEnabled]);

  return (
    <motion.section
      id="Home"
      initial={{ opacity: 0 , y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.9,
        ease: "easeOut",
      }}
      viewport={{ once: true, amount: 0.2 }}
      className="bg-gradient-to-br from-secondary via-white to-secondary/50 w-full pt-12 sm:pt-16 lg:pt-20 pb-8 lg:pb-10 relative overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-primary/5 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-gradient2/5 to-transparent rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 relative z-10">
        {/* Pill header - centered on all screens */}
        {/* Pill Header - Copy this block, change the text only */}
<div className="flex justify-center mb-6 md:mb-10 px-4">
  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-royalBlue/10 to-royalBlue/5 backdrop-blur-sm rounded-full px-4 py-1.5 sm:px-5 md:px-7 sm:py-2 md:py-3 border border-royalBlue/20 shadow-lg">
    <Heart size={16} className="text-royalGreen sm:size-[18px] flex-shrink-0" />
    <p className="font-poppins font-semibold text-royalBlue text-[11px] xs:text-xs sm:text-sm md:text-base lg:text-lg tracking-wide uppercase whitespace-nowrap">
      {/* Long line: laptops and bigger screens (≥ lg) */}
      <span className="hidden lg:inline">HEALTHCARE COORDINATION PLATFORM</span>
      
      {/* Medium line: tablet and small screens (xs up to lg) */}
      <span className="xs:inline lg:hidden">HEALTHCARE PLATFORM</span>
      
      {/* Shortest fallback: very small screens (< xs) - left untouched */}
      <span className="hidden xs:inline">HEALTHCARE PLATFORM</span>
    </p>
  </div>
</div>
        {/* Two column layout: text left, image right (stack on mobile) */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left side - Text & Buttons (appears first on mobile) */}
          <div className="flex-1 text-center lg:text-left">
            <h1 className="font-poppins font-semibold text-darkblue text-2xl sm:text-3xl md:text-4xl lg:text-[2.5rem] xl:text-[2.8rem] leading-[1.3] tracking-[-0.01em] bg-gradient-to-r from-darkblue via-darkblue/90 to-primary bg-clip-text text-transparent">
  India's First Healthcare Coordination Platform For Our Subscribers
</h1>

            <p className="text-text1/90 font-poppins mt-6 leading-relaxed text-base sm:text-lg max-w-2xl mx-auto lg:mx-0">
              Livmo Health connects our subscribers with 2500+ verified healthcare
              providers including hospitals, clinics, labs, homecare, therapists,
              and specialists.
              <br />
              <br />
              Our team coordinates and helps you find the right healthcare service
              quickly and easily.
            </p>

            {/* Magnetic Buttons - Two-line layout with wrapper */}
<div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start mt-10">
  {/* Button 1 Wrapper */}
  <div
    ref={wrapper1Ref}
    className="magnetic-wrapper inline-block rounded-xl transition-all duration-300"
  >
    <button
      ref={btn1Ref}
      onClick={handleClick}
      href={'https://pay.livmohealth.com'}
      className="magnetic-btn group relative px-6 py-3 bg-gradient-to-r from-royalGreen to-royalBlue text-white font-poppins rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-left">
          <span className="block text-sm font-medium opacity-80 tracking-wide">For Subscribers</span>
          <span className="block text-base font-semibold leading-tight">Join Now</span>
        </div>
        <ArrowRight size={18} className="shrink-0 group-hover:translate-x-1 transition-transform duration-200" />
      </div>
    </button>
  </div>

  {/* Button 2 Wrapper */}
  <div
    ref={wrapper2Ref}
    className="magnetic-wrapper inline-block rounded-xl transition-all duration-300"
  >
    <button
      ref={btn2Ref}
      onClick={() => window.open('https://app.livmohealth.com/provider/auth/sign-up', '_blank')}
      className="magnetic-btn group relative px-6 py-3 bg-gradient-to-r from-royalGreen to-royalBlue text-white font-poppins rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-left">
          <span className="block text-sm font-medium opacity-80 tracking-wide">For Providers</span>
          <span className="block text-base font-semibold leading-tight">Partner With Us</span>
        </div>
        <ArrowRight size={18} className="shrink-0 group-hover:translate-x-1 transition-transform duration-200" />
      </div>
    </button>
  </div>
</div>
    </div>

          {/* Right side - Image (compact on mobile) */}
          <div className="flex-1 flex justify-center">
            <div className="relative w-full max-w-[280px] sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl group">
              {/* Glow effects */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-gradient2/20 to-primary/20 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition duration-700"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent rounded-2xl blur-3xl"></div>

              {/* Image container */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">
                <img
                  src={healthcareHero}
                  alt="Healthcare coordination"
                  className="w-full h-auto object-cover transition duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 border border-white/20 rounded-2xl pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
  .magnetic-wrapper {
    padding: 3px;
    display: inline-block;
    border: 1px solid transparent;
    border-radius: 16px;
    transition: all 0.3s ease;
  }
  .magnetic-wrapper.active {
    border-color: rgba(34, 197, 94, 0.3);
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(59, 130, 246, 0.15));
  }
  .magnetic-btn {
    transition: transform 0.12s cubic-bezier(0.2, 0.9, 0.4, 1.1);
    will-change: transform;
    transform: translate(0, 0);
    display: inline-block;
    cursor: pointer;
  }
  .magnetic-btn.active {
    transition: transform 0.06s linear;
  }
    @media (max-width: 1023px) {
      .magnetic-btn {
        transform: translate(0, 0) !important;
      }
      .magnetic-wrapper.active {
        border-color: transparent;
        background: none;
      }
    }
  @media (max-width: 640px) {
    .magnetic-wrapper {
      padding: 6px;
    }
    .magnetic-btn {
      padding-left: 1.25rem;
      padding-right: 1.25rem;
      padding-top: 0.7rem;
      padding-bottom: 0.7rem;
    }
  }
`}</style>
    </motion.section>
  );
};

export default Hero;